import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, ListPlus } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

interface CollinDocument {
  documentNumber: string;
  filingDate: string;
  grantor: string;
  grantee: string;
  docType: string;
  releaseDate: string | null;
  legalDescription: string;
}

interface Props {
  onRecordsSaved: () => void;
}

const CORPORATE_KEYWORDS = [
  'BANK', 'MORTGAGE', 'LENDING', 'CREDIT UNION', 'FINANCE', 'FUNDING',
  'LLC', 'LTD', 'INC', 'CORP', 'CORPORATION', 'FEDERAL', 'NATIONAL',
  'SERVICES', 'SOLUTIONS', 'CAPITAL', 'INVESTMENTS', 'HOLDINGS',
  'WELLS FARGO', 'CHASE', 'PENNYMAC', 'DHI', 'USAA', 'NAVY FEDERAL',
  'VETERANS UNITED', 'GUILD', 'CHURCHILL', 'FAIRWAY', 'NEWREZ',
  'BENCHMARK', 'SWBC', 'CANOPY', 'FLAGSTAR', 'FIDELITY',
  'FIRST NATIONAL', 'FIRST FINANCIAL', 'FIRST CONTINENTAL',
  'VANDERBI', 'PLANET HOME', 'VILLAGE CAPITAL', 'ANGEL OAK',
  'UNITED WHOLESALE', 'SENTE', 'LOAN STORE',
  'HOUSING & URBAN', 'HUD', 'SECRETARY OF HOUSING',
  'MORTGAGE RESEARCH CENTER', 'MORTGAGE SOLUTIONS',
  'READYCAP', 'ROCKET MORTGAGE', 'PENNYMAC LOAN', 'PILGRIM MORTGAGE',
  'CALIBER HOME', 'QUICKEN LOANS', 'LOANDEPOT', 'MR COOPER',
  'PRIMELENDING', 'PROVIDENT FUNDING', 'CRESTMARK', 'ASPIRE FINANCIAL',
  'GUARANTEED RATE', 'HOME POINT', 'CARDINAL FINANCIAL', 'NATIONS LENDING',
  'EUSTIS MORTGAGE', 'SISTAR MORTGAGE', 'REPUBLIC STATE', 'ABSOLUTE MORTGAGE',
  'GATEWAY MORTGAGE', 'JPMORGAN', 'BANCORPSOUTH', 'THRIVE MORTGAGE',
  'SERVICE FIRST', 'CORNERSTONE HOME', 'COLONIAL NATIONAL', 'WATERSTONE MORTGAGE',
  'HIGHLANDS RESIDENTIAL', 'MOVEMENT MORTGAGE', 'CROSSCOUNTRY MORTGAGE',
  'FIRST HORIZON', 'ACADEMY MORTGAGE', 'PROSPERITY HOME', 'PULTE MORTGAGE',
  'FREEDOM MORTGAGE', 'AMERISAVE', 'BETTER MORTGAGE', 'WILLOW BEND',
  'WALLICK & VOLK', 'CERTIFIED FUNDING', 'WYNDHAM CAPITAL', 'EVERETT FINANCIAL',
  'GREAT WESTERN', 'DAS ACQUISITION', 'DALLAS MORTGAGE', 'FIRST RATE FINANCIAL',
  'AMERIDREAM', 'GUARDIAN MORTGAGE', 'AMWEST FUNDING', 'ARK LA TEX',
  'ARK-LA-TEX', 'HOMEWOOD MORTGAGE', 'MOMENTUM LOANS', 'SWBC MORTGAGE',
  'NORTH DALLAS', 'TEXAS REPUBLIC', 'GOLDWATER BANK', 'SIMMONS BANK',
  'NTFN', 'FIRSTBANK', 'SILVERTON MORTGAGE', 'HOMEWAY MORTGAGE',
  'LAKEVIEW LOAN', 'TOWN SQUARE', 'MCI MORTGAGE', 'TRINITY OAKS',
  'COMMERCE HOME', 'COMMUNITY RESOURCE', 'GREATWALL', 'GEO CORP',
  'GRIFFIN FUNDING', 'GRAND HOME LOANS', 'AMRES CORPORATION',
  'AMSW LENDING', 'CITIZENS BANK', 'TRUIST BANK', 'TIGER LOANS',
  'TEXAS STATE AFFORDABLE', 'TEXAS DEPARTMENT', 'TEXAS BANK FINANCIAL',
  'KELLER MORTGAGE', 'CITY BANK', 'STATE FARM', 'ANGEL OAK MORTGAGE',
  'AMERICAN FINANCIAL NETWORK', 'AMERICAN HERITAGE CAPITAL',
  'TEXAS MORTGAGE PROFESSIONALS', 'MCAP MORTGAGE', 'RANDOLPH BROOKS',
  'UNIVERSITY FEDERAL', 'BANK OF AMERICA', 'US BANK', 'U S BANK',
  'K HOVNANIAN', 'FINANCE AMERICA', 'HOMESMART FINANCIAL',
  'HOMETRUST MORTGAGE', 'BROOKHOLLOW MORTGAGE', 'PRIMARY RESIDENTIAL',
  'VILLAGE CAPITAL & INVESTMENT', 'SELECT PORTFOLIO', 'UIF CORPORATION',
  'SAISON FUNDEX', 'SWC RHP', 'TEXAN MORTGAGE',
];

export function CollinCountyTextParser({ onRecordsSaved }: Props) {
  const [textData, setTextData] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const { toast } = useToast();

  const parseCollinData = (text: string): CollinDocument[] => {
    const lines = text.trim().split('\n');
    const documents: CollinDocument[] = [];

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;

      // Find "DEED OF TRUST" as the anchor
      const dotIdx = trimmed.indexOf('DEED OF TRUST');
      if (dotIdx === -1) continue;

      const namesPart = trimmed.substring(0, dotIdx).trim();
      const afterDot = trimmed.substring(dotIdx + 'DEED OF TRUST'.length).trim();

      // After DEED OF TRUST: date, doc number, release date, legal description
      // Date format: M/D/YYYY
      const dateMatch = afterDot.match(/^(\d{1,2}\/\d{1,2}\/\d{4})/);
      if (!dateMatch) continue;

      const rawDate = dateMatch[1];
      const afterDate = afterDot.substring(rawDate.length).trim();

      // Document number: long numeric string
      const docMatch = afterDate.match(/^(\d{10,25})/);
      if (!docMatch) continue;

      const documentNumber = docMatch[1];
      const afterDoc = afterDate.substring(documentNumber.length).trim();

      // Release date: either --/--/-- or M/D/YYYY
      const releaseMatch = afterDoc.match(/^(--\/--\/--|(\d{1,2}\/\d{1,2}\/\d{4}))/);
      let releaseDate: string | null = null;
      let afterRelease = afterDoc;
      if (releaseMatch) {
        if (releaseMatch[2]) {
          releaseDate = releaseMatch[2];
        }
        afterRelease = afterDoc.substring(releaseMatch[0].length).trim();
      }

      const legalDescription = afterRelease;

      // Now split grantor and grantee from namesPart
      // Grantor = borrower, Grantee = lender (for deed of trust)
      // Try to find where the corporate lender name starts
      const { grantor, grantee } = splitNames(namesPart);

      // Parse date to YYYY-MM-DD
      const dateParts = rawDate.split('/');
      const filingDate = dateParts.length === 3
        ? `${dateParts[2]}-${dateParts[0].padStart(2, '0')}-${dateParts[1].padStart(2, '0')}`
        : new Date().toISOString().split('T')[0];

      documents.push({
        documentNumber,
        filingDate,
        grantor,
        grantee,
        docType: 'DEED OF TRUST',
        releaseDate,
        legalDescription,
      });
    }

    return documents;
  };

  const splitNames = (namesPart: string): { grantor: string; grantee: string } => {
    const upper = namesPart.toUpperCase();

    // Try each corporate keyword, find the earliest match that makes sense
    let bestSplitIdx = -1;

    for (const kw of CORPORATE_KEYWORDS) {
      const idx = upper.indexOf(kw);
      if (idx > 0) {
        // Make sure the split point isn't in the middle of a word
        // Check that the character before isn't a letter (to avoid partial matches)
        if (bestSplitIdx === -1 || idx < bestSplitIdx) {
          bestSplitIdx = idx;
        }
      }
    }

    if (bestSplitIdx > 0) {
      return {
        grantor: namesPart.substring(0, bestSplitIdx).trim(),
        grantee: namesPart.substring(bestSplitIdx).trim(),
      };
    }

    // Enhanced fallback for individual names
    // Try to intelligently split individual names by looking for patterns
    const words = namesPart.trim().split(/\s+/);
    
    if (words.length >= 4) {
      // Look for potential split points - try to find where borrower ends and lender begins
      // Common patterns: LASTNAME FIRSTNAME + LASTNAME FIRSTNAME
      // or multiple names that could be split in half
      const halfPoint = Math.floor(words.length / 2);
      
      // Try different split strategies
      for (let splitAt = halfPoint - 1; splitAt <= halfPoint + 1; splitAt++) {
        if (splitAt > 0 && splitAt < words.length - 1) {
          const firstPart = words.slice(0, splitAt).join(' ').trim();
          const secondPart = words.slice(splitAt).join(' ').trim();
          
          // Prefer splits where both parts look like complete names (2+ words each)
          if (firstPart.split(' ').length >= 2 && secondPart.split(' ').length >= 2) {
            return {
              grantor: firstPart,
              grantee: secondPart,
            };
          }
        }
      }
      
      // Fallback: split roughly in half
      const splitIdx = Math.floor(words.length / 2);
      return {
        grantor: words.slice(0, splitIdx).join(' ').trim(),
        grantee: words.slice(splitIdx).join(' ').trim(),
      };
    }

    // If we can't split intelligently, put everything in grantor for AI to handle
    // But don't leave grantee completely empty - use a placeholder
    return { 
      grantor: namesPart.trim(), 
      grantee: 'INDIVIDUAL_NAMES_NEED_SPLITTING' 
    };
  };

  const isCorpLender = (name: string): boolean => {
    const upper = name.toUpperCase();
    // Don't filter out our placeholder - let AI handle it
    if (upper === 'INDIVIDUAL_NAMES_NEED_SPLITTING') return false;
    return CORPORATE_KEYWORDS.some(kw => upper.includes(kw));
  };

  const handleProcess = async () => {
    if (!textData.trim()) {
      toast({ title: "No data", description: "Please paste records", variant: "destructive" });
      return;
    }

    setIsProcessing(true);
    try {
      const documents = parseCollinData(textData);
      if (documents.length === 0) {
        toast({ title: "No records found", description: "Could not parse any DEED OF TRUST records from the pasted text.", variant: "destructive" });
        setTextData("");
        setIsProcessing(false);
        return;
      }

      // Filter: remove records with corporate/institutional lenders (grantee = lender for DOT)
      const potentialOwnerFinanced = documents.filter(doc => {
        if (!doc.grantee) return true; // Couldn't split — send to AI
        return !isCorpLender(doc.grantee);
      });

      const corporateCount = documents.length - potentialOwnerFinanced.length;
      console.log(`Collin: Parsed ${documents.length} docs, ${corporateCount} corporate, ${potentialOwnerFinanced.length} potential owner-financed`);

      if (potentialOwnerFinanced.length === 0) {
        toast({
          title: "No owner-financed deals found",
          description: `Analyzed ${documents.length} records — all had corporate/institutional lenders`,
        });
        setTextData("");
        setIsProcessing(false);
        return;
      }

      // Send remaining to AI for final verification
      const deedText = potentialOwnerFinanced.map(d =>
        `Document: ${d.documentNumber}\n` +
        `Date: ${d.filingDate}\n` +
        `Grantor (Borrower): ${d.grantor}\n` +
        `Grantee (Lender/Beneficiary): ${d.grantee}\n` +
        `Legal: ${d.legalDescription}\n`
      ).join('\n---\n');

      const { data: filterData, error: filterError } = await supabase.functions.invoke(
        'filter-owner-finance',
        { body: { deedText } }
      );

      if (filterError) throw filterError;

      const ownerFinancedDeals = filterData?.filteredDeals || [];
      const ownerFinancedDocNumbers = new Set(
        ownerFinancedDeals.map((deal: any) => deal.documentNumber)
      );

      const confirmedRecords = potentialOwnerFinanced.filter(d =>
        ownerFinancedDocNumbers.has(d.documentNumber)
      );

      console.log(`AI confirmed ${confirmedRecords.length} owner-financed deals`);

      if (confirmedRecords.length === 0) {
        toast({
          title: "No owner-financed deals confirmed",
          description: `Hard filter passed ${potentialOwnerFinanced.length}, but AI rejected all as non-individual lenders`,
        });
        setTextData("");
        setIsProcessing(false);
        return;
      }

      // Check for existing records
      const existingQuery = await supabase
        .from('county_research')
        .select('document_number')
        .eq('county', 'collin')
        .in('document_number', confirmedRecords.map(r => r.documentNumber));

      const existingDocs = new Set(existingQuery.data?.map(d => d.document_number) || []);
      const newRecords = confirmedRecords.filter(r => !existingDocs.has(r.documentNumber));

      if (newRecords.length === 0) {
        toast({
          title: "No new records",
          description: `Found ${confirmedRecords.length} owner-financed deals, but all already exist in database`,
        });
        setTextData("");
        setIsProcessing(false);
        return;
      }

      // Insert
      const recordsToInsert = newRecords.map(record => ({
        county: 'collin',
        document_number: record.documentNumber,
        filing_date: record.filingDate,
        document_type: record.docType,
        grantor_name: record.grantor, // Grantor = Borrower
        grantee_name: record.grantee, // Grantee = Beneficiary/Lender (who we want to contact)
        legal_description: record.legalDescription,
        is_potential_owner_finance: true,
        confidence_score: 80,
        detection_reasons: ['AI confirmed individual beneficiary/lender'],
        status: 'new',
        raw_data: { grantor: record.grantor, grantee: record.grantee } as any,
      }));

      const { error: insertError } = await supabase
        .from('county_research')
        .upsert(recordsToInsert, { onConflict: 'document_number', ignoreDuplicates: false });

      if (insertError) throw insertError;

      toast({
        title: "Success!",
        description: `Saved ${newRecords.length} owner-financed deals!\n` +
          `Total: ${documents.length} | Corporate: ${corporateCount} | ` +
          `AI confirmed: ${confirmedRecords.length} | New saved: ${newRecords.length}`,
      });

      setTextData("");
      onRecordsSaved();
    } catch (error) {
      console.error('Error processing Collin County data:', error);
      toast({ title: "Error", description: "Failed to process records. Please try again.", variant: "destructive" });
    } finally {
      setIsProcessing(false);
    }
  };

  const previewCount = textData.trim() ? parseCollinData(textData).length : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Collin County, TX - Text Parser with AI Filtering</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Paste deed of trust records from the Collin County Public Records website.
            Copy rows directly from the search results (concatenated format).
          </p>
          <p className="text-sm text-muted-foreground">
            <strong>Format:</strong> GRANTOR_NAME + GRANTEE_NAME + DEED OF TRUST + DATE + DOC# + RELEASE_DATE + LEGAL_DESCRIPTION
          </p>
          <p className="text-sm text-muted-foreground">
            Corporate/institutional lenders are automatically filtered out. Remaining records are verified by AI.
          </p>
        </div>

        <Textarea
          value={textData}
          onChange={(e) => setTextData(e.target.value)}
          placeholder={"HANNON JOHN QUITMANPENNYMAC LOAN SERVICES LLCDEED OF TRUST1/4/202120210104000000040--/--/--Subdivision- Name: HUNTERS GLEN ESTATES #8 PLANO D/1 Lot: 52 Block: B, Reference - D / 1\nHIROSHIMA RYOKOREPUBLIC STATE MORTGAGE CODEED OF TRUST1/4/202120210104000000080--/--/--Subdivision- Name: NORTH CREEK ESTATES #7 MELISSA 2020/189 Lot: 18 Block: B, Reference - 2020 / 189"}
          className="min-h-[300px] font-mono text-sm"
        />

        {previewCount > 0 && (
          <p className="text-sm text-muted-foreground">
            Detected <strong>{previewCount}</strong> DEED OF TRUST records to analyze
          </p>
        )}

        <Button
          onClick={handleProcess}
          disabled={isProcessing || !textData.trim()}
          className="w-full"
        >
          {isProcessing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Filtering corporate lenders + AI verification...
            </>
          ) : (
            `Process ${previewCount > 0 ? previewCount + ' ' : ''}Records`
          )}
        </Button>

        {isProcessing && (
          <p className="text-sm text-muted-foreground text-center">
            Hard-filtering corporate lenders, then AI verifying remaining records...
          </p>
        )}
      </CardContent>
    </Card>
  );
}
